/**
 * HouseRules Component
 *
 * Display house rules and cancellation policy.
 */

import React from 'react';
import type { ListingData } from '../hooks/useListing';

interface HouseRulesProps {
  houseRules: ListingData['houseRules'];
  cancellationPolicy: string;
  minimumStay?: number;
}

const CANCELLATION_POLICIES: Record<string, { title: string; description: string }> = {
  flexible: {
    title: 'Flexible',
    description: 'Free cancellation up to 24 hours before check-in. Full refund minus service fees.',
  },
  moderate: {
    title: 'Moderate',
    description: 'Free cancellation up to 5 days before check-in. 50% refund for cancellations up to 24 hours before.',
  },
  strict: {
    title: 'Strict',
    description: 'Free cancellation for 48 hours after booking, if booked at least 14 days before check-in. 50% refund up to 7 days before.',
  },
};

export const HouseRules: React.FC<HouseRulesProps> = ({
  houseRules,
  cancellationPolicy,
  minimumStay = 1,
}) => {
  const policy = CANCELLATION_POLICIES[cancellationPolicy] || CANCELLATION_POLICIES.moderate;

  return (
    <section className="py-6 border-b border-gray-200">
      <h2 className="text-xl font-semibold text-gray-900 mb-4">
        Things to know
      </h2>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {/* House Rules */}
        <div>
          <h3 className="font-medium text-gray-900 mb-3">House rules</h3>
          <ul className="space-y-2 text-sm text-gray-600">
            <li className="flex items-center gap-2">
              <ClockIcon className="w-4 h-4 text-gray-400" />
              Check-in: {houseRules.checkInTime}
            </li>
            <li className="flex items-center gap-2">
              <ClockIcon className="w-4 h-4 text-gray-400" />
              Checkout: {houseRules.checkOutTime}
            </li>
            {minimumStay > 1 && (
              <li className="flex items-center gap-2">
                <CalendarIcon className="w-4 h-4 text-gray-400" />
                {minimumStay} night minimum
              </li>
            )}
            <li className="flex items-center gap-2">
              {houseRules.smokingAllowed ? (
                <CheckIcon className="w-4 h-4 text-green-500" />
              ) : (
                <CrossIcon className="w-4 h-4 text-red-500" />
              )}
              {houseRules.smokingAllowed ? 'Smoking allowed' : 'No smoking'}
            </li>
            <li className="flex items-center gap-2">
              {houseRules.petsAllowed ? (
                <CheckIcon className="w-4 h-4 text-green-500" />
              ) : (
                <CrossIcon className="w-4 h-4 text-red-500" />
              )}
              {houseRules.petsAllowed ? 'Pets allowed' : 'No pets'}
            </li>
            <li className="flex items-center gap-2">
              {houseRules.partiesAllowed ? (
                <CheckIcon className="w-4 h-4 text-green-500" />
              ) : (
                <CrossIcon className="w-4 h-4 text-red-500" />
              )}
              {houseRules.partiesAllowed ? 'Parties allowed' : 'No parties or events'}
            </li>
          </ul>
          {houseRules.additionalRules && houseRules.additionalRules.length > 0 && (
            <div className="mt-3 pt-3 border-t border-gray-100">
              <p className="text-xs text-gray-500 mb-2">Additional rules:</p>
              <ul className="text-sm text-gray-600 space-y-1">
                {houseRules.additionalRules.map((rule, idx) => (
                  <li key={idx}>• {rule}</li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Safety & Property */}
        <div>
          <h3 className="font-medium text-gray-900 mb-3">Safety & property</h3>
          <ul className="space-y-2 text-sm text-gray-600">
            <li className="flex items-center gap-2">
              <ShieldIcon className="w-4 h-4 text-gray-400" />
              Security camera/recording device
            </li>
            <li className="flex items-center gap-2">
              <ShieldIcon className="w-4 h-4 text-gray-400" />
              Smoke alarm
            </li>
            <li className="flex items-center gap-2">
              <ShieldIcon className="w-4 h-4 text-gray-400" />
              Carbon monoxide alarm
            </li>
          </ul>
        </div>

        {/* Cancellation Policy */}
        <div>
          <h3 className="font-medium text-gray-900 mb-3">Cancellation policy</h3>
          <p className="text-sm font-medium text-gray-900 mb-1">{policy.title}</p>
          <p className="text-sm text-gray-600">{policy.description}</p>
        </div>
      </div>
    </section>
  );
};

// Icons
const ClockIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const CalendarIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
  </svg>
);

const CheckIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
  </svg>
);

const CrossIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const ShieldIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
  </svg>
);

export default HouseRules;
